import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldManagement } from "../field/field";
import { TurbineManagement } from "../field/turbine";
import { UserManagement } from "../field/user";
import { TenantCustomerManagement } from "./tenant_customers";
import { apiClient } from "../../services/api";
import { useToast } from "../../hooks/useToast";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        ...FC.shorthands.gap("14px"),
        ...FC.shorthands.padding("16px"),
    },
    header: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        ...FC.shorthands.gap("12px"),
        ...FC.shorthands.padding("16px"),
        ...FC.shorthands.borderRadius("8px"),
        backgroundColor: FC.tokens.colorNeutralBackground2,
    },
    title: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("4px"),
    },
    managedBanner: {
        fontWeight: FC.tokens.fontWeightSemibold,
        color: FC.tokens.colorBrandForeground1,
    },
    content: {
        minWidth: 0,
        ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2),
        ...FC.shorthands.borderRadius("8px"),
        backgroundColor: FC.tokens.colorNeutralBackground1,
    },
    section: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("12px"),
        ...FC.shorthands.padding("16px"),
    },
    cards: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        ...FC.shorthands.gap("10px"),
    },
    card: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("4px"),
        ...FC.shorthands.padding("12px"),
        ...FC.shorthands.border("1px", "solid", FC.tokens.colorNeutralStroke2),
        ...FC.shorthands.borderRadius("6px"),
    },
    readiness: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        ...FC.shorthands.gap("8px"),
    },
    readinessItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
        ...FC.shorthands.padding("8px"),
        backgroundColor: FC.tokens.colorNeutralBackground2,
        ...FC.shorthands.borderRadius("4px"),
    },
    row: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("8px"),
    },
    tableWrap: {
        overflowX: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        "& th": {
            textAlign: "left",
            ...FC.shorthands.padding("8px"),
            borderBottom: `1px solid ${FC.tokens.colorNeutralStroke1}`,
        },
        "& td": {
            ...FC.shorthands.padding("8px"),
            borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
            verticalAlign: "top",
        },
    },
    muted: { color: FC.tokens.colorNeutralForeground3 },
    error: { color: FC.tokens.colorPaletteRedForeground1 },
});

const tabItems = [
    ["overview", "概览"],
    ["people", "人员"],
    ["customers", "客户账号"],
    ["assets", "资产范围"],
    ["tasks", "巡检任务"],
    ["audit", "审计"],
];

const countLabels = {
    people: "账号人员",
    enabledPeople: "启用人员",
    assetOwners: "可见业主",
    fields: "风场",
    turbines: "风机",
    inspectionSessions: "巡检批次",
};

const readinessLabels = {
    people: "人员",
    visibilityScope: "可见范围",
    fieldSetup: "风场配置",
    globalUpdateKeys: "全局 Update keys",
    platformEncryptionKey: "平台加密公钥",
    firstInspectionTask: "首个巡检批次",
};

const stateBadge = (state) => {
    const value = typeof state === "string" ? state : state?.state;
    const color = value === "ready"
        ? "success"
        : value === "warning" || value === "pending"
            ? "warning"
            : value === "critical" || value === "blocked"
                ? "danger"
                : "informative";
    return <FC.Badge color={color}>{value || "unknown"}</FC.Badge>;
};

const formatDateTime = (value) => value ? new Date(value).toLocaleString() : "-";

const WorkbenchOverview = ({ tenantId, summary, onRefresh }) => {
    const styles = useStyles();
    const readiness = summary?.readiness || {};
    return (
        <div className={styles.section}>
            <div className={styles.row}>
                <FC.Title3>开通概览</FC.Title3>
                <FC.Button icon={<IC.ArrowSyncRegular />} onClick={onRefresh}>刷新</FC.Button>
            </div>
            <div className={styles.cards}>
                {Object.entries(countLabels).map(([key, label]) => (
                    <div className={styles.card} key={key}>
                        <FC.Caption1>{label}</FC.Caption1>
                        <FC.Title2>{summary?.counts?.[key] ?? 0}</FC.Title2>
                    </div>
                ))}
            </div>
            <FC.Title3>就绪状态</FC.Title3>
            <div className={styles.readiness}>
                {Object.entries(readinessLabels).map(([key, label]) => (
                    <div className={styles.readinessItem} key={key}>
                        <span>{label}</span>
                        {stateBadge(readiness[key])}
                    </div>
                ))}
            </div>
            <FC.Title3>阻塞项与预警</FC.Title3>
            {(summary?.blockers || []).length === 0 ? (
                <FC.MessageBar intent="success">
                    <FC.MessageBarBody>当前未发现工作台开通阻塞项。</FC.MessageBarBody>
                </FC.MessageBar>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead><tr><th>Code</th><th>模块</th><th>定位</th><th>说明</th></tr></thead>
                        <tbody>
                            {summary.blockers.map((item, index) => (
                                <tr key={`${item.code}-${item.fieldId || item.requiredRole || index}`}>
                                    <td>{item.code}</td>
                                    <td>{item.scope}</td>
                                    <td>{item.fieldSeq || item.requiredRole || "-"}</td>
                                    <td>{item.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <FC.Divider />
            <FC.Title3>最近代管操作</FC.Title3>
            <ManagedAuditPanel tenantId={tenantId} compact />
        </div>
    );
};

const ManagedAssetPanel = ({ tenantId, tenantName, onChanged }) => {
    const styles = useStyles();
    const showToast = useToast();
    const [scopes, setScopes] = useState([]);
    const [owners, setOwners] = useState([]);
    const [fields, setFields] = useState([]);
    const [selectedOwnerId, setSelectedOwnerId] = useState("");
    const [selectedFieldId, setSelectedFieldId] = useState("");
    const [turbines, setTurbines] = useState([]);
    const [error, setError] = useState("");
    // §13.3 wants 风场 → 厂商/机型 → 风机/叶片 → 批次. Every level below already existed
    // server-side, tenant-scoped, and nothing consumed it — the tab stopped at 风场 → 风机.
    const [catalogue, setCatalogue] = useState({
        turbineManus: [], turbineModels: [], leafManus: [], leafModels: [],
    });
    const [selectedModelId, setSelectedModelId] = useState("");
    const [selectedTurbineId, setSelectedTurbineId] = useState("");
    const [leaves, setLeaves] = useState([]);
    const [sessions, setSessions] = useState([]);
    // Three independent loads, three counters. They shared one until 2026-08-16, so selecting a
    // turbine bumped the generation the scope/field refresh was waiting on and that refresh's
    // result was discarded as stale — a refresh that silently did nothing.
    const requestRef = useRef(0);
    const fieldLoadRef = useRef(0);
    const leafLoadRef = useRef(0);

    const load = useCallback(async () => {
        const seq = ++requestRef.current;
        setError("");
        try {
            const [scopeRows, ownerRows, fieldRows] = await Promise.all([
                apiClient.getManagedAssetOwners(tenantId),
                apiClient.getAssetOwners({ status: "active" }),
                apiClient.getManagedFields(tenantId, { includeArchived: true }),
            ]);
            if (seq !== requestRef.current) return;
            setScopes(scopeRows || []);
            setOwners(ownerRows || []);
            setFields(fieldRows || []);
            const active = (fieldRows || []).filter((field) => field.status === "active");
            setSelectedFieldId((current) => active.some((field) => String(field.id) === current)
                ? current
                : active[0] ? String(active[0].id) : "");
        } catch (loadError) {
            if (seq === requestRef.current) setError(loadError.message);
        }
    }, [tenantId]);

    useEffect(() => {
        load();
        return () => { requestRef.current += 1; };
    }, [load]);

    useEffect(() => {
        const fieldId = Number(selectedFieldId);
        setSelectedModelId("");
        setSelectedTurbineId("");
        if (!fieldId) {
            setTurbines([]);
            setCatalogue({ turbineManus: [], turbineModels: [], leafManus: [], leafModels: [] });
            setSessions([]);
            return undefined;
        }
        const seq = ++fieldLoadRef.current;
        Promise.all([
            apiClient.getManagedTurbines(tenantId, fieldId, { includeArchived: true }),
            // Manufacturers and models are a GLOBAL catalogue since 2026-08-14. The tenant-nested
            // routes still filter FieldItemId == fieldId, which now means "originated here", so
            // they would omit both the models this field's turbines use that were created
            // elsewhere and every row with no origin field — and present the remainder as the
            // field's complete catalogue. Read the shared catalogue and derive this level from
            // what the field's turbines and blades actually use.
            apiClient.getManuTurbines(),
            apiClient.getModelTurbines(),
            apiClient.getManuLeaves(),
            apiClient.getModelLeaves(),
            // Narrowed server-side, before the limit. Fetching the tenant's most recent N and
            // filtering here reports "no batches" for any field whose batches fall outside that
            // window, which looks exactly like a field that has none.
            apiClient.getManagedInspectionSessions(tenantId, { limit: 200, fieldId }),
        ])
            .then(([turbineRows, turbineManus, turbineModels, leafManus, leafModels, sessionRows]) => {
                if (seq !== fieldLoadRef.current) return;
                setTurbines(turbineRows || []);
                setCatalogue({
                    turbineManus: turbineManus || [],
                    turbineModels: turbineModels || [],
                    leafManus: leafManus || [],
                    leafModels: leafModels || [],
                });
                setSessions(sessionRows || []);
            })
            .catch((loadError) => {
                if (seq === fieldLoadRef.current) setError(loadError.message);
            });
        return () => { fieldLoadRef.current += 1; };
    }, [selectedFieldId, tenantId]);

    useEffect(() => {
        const fieldId = Number(selectedFieldId);
        const turbineId = Number(selectedTurbineId);
        if (!fieldId || !turbineId) {
            setLeaves([]);
            return undefined;
        }
        const seq = ++leafLoadRef.current;
        apiClient.getManagedTurbineLeaves(tenantId, fieldId, turbineId)
            .then((items) => {
                if (seq === leafLoadRef.current) setLeaves(items || []);
            })
            .catch((loadError) => {
                if (seq === leafLoadRef.current) setError(loadError.message);
            });
        return () => { leafLoadRef.current += 1; };
    }, [selectedTurbineId, selectedFieldId, tenantId]);

    // 厂商/机型 is derived from what this field's turbines actually use, looked up in the shared
    // catalogue. Listing the catalogue rows whose FieldItemId is this field would be a different
    // question with a wrong answer: since 2026-08-14 that column records where a row was first
    // created, not who may use it.
    const turbineModelById = useMemo(
        () => new Map((catalogue.turbineModels || []).map((model) => [model.id, model])),
        [catalogue.turbineModels]);
    const leafModelById = useMemo(
        () => new Map((catalogue.leafModels || []).map((model) => [model.id, model])),
        [catalogue.leafModels]);

    const modelsInUse = useMemo(() => {
        const rows = new Map();
        for (const turbine of turbines) {
            if (turbine.status === "archived") continue;
            const key = turbine.modelId ?? 0;
            const known = turbineModelById.get(turbine.modelId);
            const existing = rows.get(key);
            if (existing) {
                existing.turbineCount += 1;
                continue;
            }
            rows.set(key, {
                modelId: key,
                // The turbine row carries its own model seq, so an unknown catalogue row shows
                // as the seq rather than blank.
                modelSeq: known?.seq || turbine.modelSeq || "(未知机型)",
                manuName: known?.manuName || "-",
                power: known?.power ?? null,
                turbineCount: 1,
            });
        }
        return [...rows.values()].sort((a, b) => String(a.modelSeq).localeCompare(String(b.modelSeq)));
    }, [turbines, turbineModelById]);

    const drillTurbines = useMemo(() => {
        const active = turbines.filter((turbine) => turbine.status !== "archived");
        if (!selectedModelId) return active;
        return active.filter((turbine) => String(turbine.modelId ?? 0) === selectedModelId);
    }, [turbines, selectedModelId]);

    const selectedTurbine = useMemo(
        () => turbines.find((turbine) => String(turbine.id) === selectedTurbineId) || null,
        [turbines, selectedTurbineId]);

    const grantedIds = new Set(scopes
        .filter((item) => item.effectiveStatus === "active")
        .map((item) => Number(item.assetOwnerId)));
    const grantableOwners = owners.filter((owner) => !grantedIds.has(Number(owner.id)));
    const activeFields = fields.filter((field) => field.status === "active");
    const archivedFields = fields.filter((field) => field.status === "archived");
    const archivedTurbines = turbines.filter((turbine) => turbine.status === "archived");

    const grant = async () => {
        if (!selectedOwnerId) return;
        try {
            await apiClient.grantManagedAssetOwner(tenantId, Number(selectedOwnerId));
            setSelectedOwnerId("");
            showToast(`已为 ${tenantName} 授予业主范围`, "success");
            await load();
            onChanged?.();
        } catch (writeError) {
            showToast("授权失败", "error", writeError.message);
        }
    };

    const revoke = async (scope) => {
        try {
            const impact = await apiClient.getManagedAssetOwnerRevokeImpact(tenantId, scope.assetOwnerId);
            const confirmed = window.confirm(
                `当前正在代管：${tenantName}\n撤销 ${scope.assetOwnerName} 可见范围？\n` +
                `影响：${impact.fieldCount || 0} 个风场，${impact.unfinishedTaskCount || 0} 个未完成任务。`
            );
            if (!confirmed) return;
            await apiClient.revokeManagedAssetOwner(tenantId, scope.assetOwnerId);
            showToast("可见范围已撤销；历史任务仍可只读访问", "success");
            await load();
            onChanged?.();
        } catch (writeError) {
            showToast("撤销失败", "error", writeError.message);
        }
    };

    const restoreField = async (field) => {
        if (!window.confirm(`当前正在代管：${tenantName}\n恢复风场 ${field.seq}？`)) return;
        try {
            await apiClient.restoreManagedField(tenantId, field.id);
            showToast("风场已恢复", "success");
            await load();
            onChanged?.();
        } catch (writeError) {
            showToast("恢复风场失败", "error", writeError.message);
        }
    };

    const restoreTurbine = async (turbine) => {
        const fieldId = Number(selectedFieldId);
        if (!window.confirm(`当前正在代管：${tenantName}\n恢复风机 ${turbine.seq}？`)) return;
        try {
            await apiClient.restoreManagedTurbine(tenantId, fieldId, turbine.id);
            showToast("风机已恢复", "success");
            const items = await apiClient.getManagedTurbines(tenantId, fieldId, { includeArchived: true });
            setTurbines(items || []);
            onChanged?.();
        } catch (writeError) {
            showToast("恢复风机失败", "error", writeError.message);
        }
    };

    return (
        <div className={styles.section}>
            {error && <FC.MessageBar intent="error"><FC.MessageBarBody>{error}</FC.MessageBarBody></FC.MessageBar>}
            <FC.Title3>业主可见范围</FC.Title3>
            <div className={styles.row}>
                <FC.Dropdown
                    placeholder="选择要授权的业主"
                    value={grantableOwners.find((owner) => String(owner.id) === selectedOwnerId)?.name || ""}
                    selectedOptions={selectedOwnerId ? [selectedOwnerId] : []}
                    onOptionSelect={(_, data) => setSelectedOwnerId(data.optionValue)}
                >
                    {grantableOwners.map((owner) => (
                        <FC.Option key={owner.id} value={String(owner.id)}>{owner.seq} - {owner.name}</FC.Option>
                    ))}
                </FC.Dropdown>
                <FC.Button appearance="primary" disabled={!selectedOwnerId} onClick={grant}>授予范围</FC.Button>
            </div>
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead><tr><th>业主</th><th>状态</th><th>有效期</th><th>操作</th></tr></thead>
                    <tbody>{scopes.map((scope) => (
                        <tr key={scope.assetOwnerId}>
                            <td>{scope.assetOwnerSeq} - {scope.assetOwnerName}</td>
                            <td>{stateBadge(scope.effectiveStatus === "active" ? "ready" : scope.effectiveStatus)}</td>
                            <td>{formatDateTime(scope.validFromUtc)} ～ {formatDateTime(scope.validToUtc)}</td>
                            <td><FC.Button disabled={scope.effectiveStatus !== "active"} onClick={() => revoke(scope)}>撤销</FC.Button></td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
            <FC.Divider />
            <FC.Title3>风场</FC.Title3>
            <FieldManagement tenantId={tenantId} />
            {archivedFields.length > 0 && (
                <>
                    <FC.Title3>已归档风场</FC.Title3>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>编号</th><th>名称</th><th>归档时间</th><th>原因</th><th>操作</th></tr></thead>
                            <tbody>{archivedFields.map((field) => (
                                <tr key={field.id}>
                                    <td>{field.seq}</td><td>{field.name}</td>
                                    <td>{formatDateTime(field.archivedAtUtc)}</td><td>{field.archiveReason || "-"}</td>
                                    <td><FC.Button onClick={() => restoreField(field)}>恢复</FC.Button></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                </>
            )}
            <FC.Divider />
            <FC.Title3>风机（固定租户/风场上下文）</FC.Title3>
            <FC.Dropdown
                value={activeFields.find((field) => String(field.id) === selectedFieldId)?.name || ""}
                selectedOptions={selectedFieldId ? [selectedFieldId] : []}
                onOptionSelect={(_, data) => setSelectedFieldId(data.optionValue)}
            >
                {activeFields.map((field) => (
                    <FC.Option key={field.id} value={String(field.id)}>{field.seq} - {field.name}</FC.Option>
                ))}
            </FC.Dropdown>
            {selectedFieldId ? (
                <TurbineManagement
                    key={`${tenantId}:${selectedFieldId}`}
                    tenantId={tenantId}
                    fieldId={Number(selectedFieldId)}
                />
            ) : <FC.Body1 className={styles.muted}>请先创建或恢复一个 active 风场。</FC.Body1>}
            <FC.Divider />
            <FC.Title3>资产下钻：风场 → 厂商/机型 → 风机/叶片 → 批次</FC.Title3>
            {!selectedFieldId ? (
                <FC.Body1 className={styles.muted}>请先选择一个 active 风场。</FC.Body1>
            ) : (
                <>
                    <FC.Subtitle2>厂商 / 机型（按本风场风机的实际使用）</FC.Subtitle2>
                    <FC.Body1 className={styles.muted}>
                        厂商与机型是平台共享目录；这里只列出本风场风机正在使用的条目，不是目录全集。
                    </FC.Body1>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>机型</th><th>厂商</th><th>功率</th><th>风机数</th><th>操作</th></tr></thead>
                            <tbody>
                                {modelsInUse.length === 0 && (
                                    <tr><td colSpan={5} className={styles.muted}>本风场尚无 active 风机。</td></tr>
                                )}
                                {modelsInUse.map((model) => (
                                    <tr key={model.modelId}>
                                        <td>{model.modelSeq}</td>
                                        <td>{model.manuName}</td>
                                        <td>{model.power ?? "-"}</td>
                                        <td>{model.turbineCount}</td>
                                        <td>
                                            <FC.Button
                                                appearance={String(model.modelId) === selectedModelId ? "primary" : "secondary"}
                                                onClick={() => {
                                                    setSelectedTurbineId("");
                                                    setSelectedModelId((current) => (
                                                        current === String(model.modelId) ? "" : String(model.modelId)));
                                                }}
                                            >
                                                {String(model.modelId) === selectedModelId ? "取消筛选" : "下钻"}
                                            </FC.Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <FC.Subtitle2>风机{selectedModelId ? "（已按机型筛选）" : ""}</FC.Subtitle2>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>编号</th><th>名称</th><th>机型</th><th>安装时间</th><th>操作</th></tr></thead>
                            <tbody>
                                {drillTurbines.length === 0 && (
                                    <tr><td colSpan={5} className={styles.muted}>没有匹配的 active 风机。</td></tr>
                                )}
                                {drillTurbines.map((turbine) => (
                                    <tr key={turbine.id}>
                                        <td>{turbine.seq}</td>
                                        <td>{turbine.name}</td>
                                        <td>{turbine.modelSeq || "-"}</td>
                                        <td>{formatDateTime(turbine.installedAt)}</td>
                                        <td>
                                            <FC.Button
                                                appearance={String(turbine.id) === selectedTurbineId ? "primary" : "secondary"}
                                                onClick={() => setSelectedTurbineId((current) => (
                                                    current === String(turbine.id) ? "" : String(turbine.id)))}
                                            >
                                                {String(turbine.id) === selectedTurbineId ? "收起叶片" : "查看叶片"}
                                            </FC.Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {selectedTurbineId && (
                        <>
                            <FC.Subtitle2>叶片（风机 {selectedTurbine?.seq || selectedTurbineId}）</FC.Subtitle2>
                            <div className={styles.tableWrap}>
                                <table className={styles.table}>
                                    <thead><tr><th>编号</th><th>挂载位</th><th>叶片机型</th><th>厂商</th><th>安装时间</th></tr></thead>
                                    <tbody>
                                        {leaves.length === 0 && (
                                            <tr><td colSpan={5} className={styles.muted}>该风机尚未登记叶片。</td></tr>
                                        )}
                                        {leaves.map((leaf) => (
                                            <tr key={leaf.id}>
                                                <td>{leaf.seq}</td>
                                                <td>{leaf.attachId ?? "-"}</td>
                                                <td>{leaf.modelSeq || "-"}</td>
                                                <td>{leafModelById.get(leaf.modelId)?.manuName || "-"}</td>
                                                <td>{formatDateTime(leaf.installedAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <FC.Subtitle2>巡检批次（本风场）</FC.Subtitle2>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>批次</th><th>名称</th><th>巡检日期</th><th>状态</th><th>复核</th></tr></thead>
                            <tbody>
                                {sessions.length === 0 && (
                                    <tr><td colSpan={5} className={styles.muted}>本风场尚无巡检批次。</td></tr>
                                )}
                                {sessions.map((session) => (
                                    <tr key={session.id}>
                                        <td>{session.seq}</td>
                                        <td>{session.name}</td>
                                        <td>{formatDateTime(session.inspectionDate)}</td>
                                        <td>{session.status || "-"}</td>
                                        <td>{session.reviewStatus || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {archivedTurbines.length > 0 && (
                <>
                    <FC.Title3>当前风场已归档风机</FC.Title3>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead><tr><th>编号</th><th>名称</th><th>归档时间</th><th>原因</th><th>操作</th></tr></thead>
                            <tbody>{archivedTurbines.map((turbine) => (
                                <tr key={turbine.id}>
                                    <td>{turbine.seq}</td><td>{turbine.name}</td>
                                    <td>{formatDateTime(turbine.archivedAtUtc)}</td><td>{turbine.archiveReason || "-"}</td>
                                    <td><FC.Button onClick={() => restoreTurbine(turbine)}>恢复</FC.Button></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const ManagedTaskPanel = ({ tenantId, tenantName }) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [error, setError] = useState("");
    const requestRef = useRef(0);
    useEffect(() => {
        const seq = ++requestRef.current;
        apiClient.getManagedInspectionSessions(tenantId)
            .then((items) => { if (seq === requestRef.current) setSessions(items || []); })
            .catch((loadError) => { if (seq === requestRef.current) setError(loadError.message); });
        return () => { requestRef.current += 1; };
    }, [tenantId]);

    return (
        <div className={styles.section}>
            <div className={styles.row}>
                <FC.Title3>巡检批次与任务状态</FC.Title3>
                <FC.Button
                    appearance="primary"
                    icon={<IC.OpenRegular />}
                    onClick={() => navigate(`/task/management?tenantId=${tenantId}&managed=1`)}
                >
                    在任务页管理 {tenantName}
                </FC.Button>
            </div>
            <FC.MessageBar intent="info">
                <FC.MessageBarBody>
                    跳转会携带固定 tenantId；任务页不得改选其他租户。创建批次仍复用现有任务流程。
                </FC.MessageBarBody>
            </FC.MessageBar>
            {error && <FC.MessageBar intent="error"><FC.MessageBarBody>{error}</FC.MessageBarBody></FC.MessageBar>}
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead><tr><th>批次</th><th>风场</th><th>巡检日期</th><th>状态</th><th>任务</th><th>完成风机</th></tr></thead>
                    <tbody>{sessions.map((session) => (
                        <tr key={session.id}>
                            <td>{session.seq} - {session.name}</td>
                            <td>{session.fieldSeq} - {session.fieldName}</td>
                            <td>{session.inspectionDate ? new Date(session.inspectionDate).toLocaleDateString() : "-"}</td>
                            <td>{session.status}{session.reviewStatus ? ` / ${session.reviewStatus}` : ""}</td>
                            <td>{session.taskCount || 0}</td>
                            <td>{session.completedTurbines || 0} / {session.totalTurbines || 0}</td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
        </div>
    );
};

const ManagedAuditPanel = ({ tenantId, compact = false }) => {
    const styles = useStyles();
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [coverage, setCoverage] = useState(null);
    const [filters, setFilters] = useState({ action: "", entityType: "", entityKey: "" });
    const [error, setError] = useState("");
    const requestRef = useRef(0);

    const load = useCallback(async ({ cursor = null, append = false } = {}) => {
        const seq = ++requestRef.current;
        setError("");
        try {
            const result = await apiClient.getManagedTenantAudit(tenantId, {
                ...(cursor ? { cursor } : {}),
                ...(filters.action ? { action: filters.action } : {}),
                ...(filters.entityType ? { entityType: filters.entityType } : {}),
                ...(filters.entityKey ? { entityKey: filters.entityKey } : {}),
                pageSize: compact ? 10 : 50,
            });
            if (seq !== requestRef.current) return;
            setItems((current) => append ? [...current, ...(result.items || [])] : (result.items || []));
            setNextCursor(result.nextCursor || null);
            setCoverage(result.auditCoverageStartedAtUtc || null);
        } catch (loadError) {
            if (seq === requestRef.current) setError(loadError.message);
        }
    }, [compact, filters, tenantId]);

    useEffect(() => {
        load();
        return () => { requestRef.current += 1; };
    }, [load]);

    return (
        <div className={styles.section}>
            {!compact && (
                <div className={styles.row}>
                    <FC.Input placeholder="动作码" value={filters.action} onChange={(_, data) => setFilters((v) => ({ ...v, action: data.value }))} />
                    <FC.Input placeholder="实体类型" value={filters.entityType} onChange={(_, data) => setFilters((v) => ({ ...v, entityType: data.value }))} />
                    <FC.Input placeholder="实体键（精确）" value={filters.entityKey} onChange={(_, data) => setFilters((v) => ({ ...v, entityKey: data.value }))} />
                    <FC.Button onClick={() => load()}>查询</FC.Button>
                </div>
            )}
            <FC.MessageBar intent="info">
                <FC.MessageBarBody>
                    租户代管审计仅覆盖 {coverage ? formatDateTime(coverage) : "当前已写入的代管记录"} 之后的新写入；更早历史请从任务和设备记录查看。
                </FC.MessageBarBody>
            </FC.MessageBar>
            {error && <FC.MessageBar intent="error"><FC.MessageBarBody>{error}</FC.MessageBarBody></FC.MessageBar>}
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead><tr><th>时间</th><th>操作人</th><th>动作</th><th>实体</th><th>原因</th></tr></thead>
                    <tbody>{items.map((item) => (
                        <tr key={item.id}>
                            <td>{formatDateTime(item.timestamp)}</td>
                            <td>{item.actorName || item.actorId}</td>
                            <td>{item.action}</td>
                            <td>{item.entityType} / {item.entityKey || item.entityId || "-"}</td>
                            <td>{item.reason || (item.hasDetails ? "已记录脱敏详情" : "-")}</td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
            {!compact && nextCursor && <FC.Button onClick={() => load({ cursor: nextCursor, append: true })}>加载更多</FC.Button>}
        </div>
    );
};

export const TenantManagementWorkbench = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { tenantId: routeTenantId } = useParams();
    const tenantId = Number(routeTenantId);
    const [summary, setSummary] = useState(null);
    const [selectedTab, setSelectedTab] = useState("overview");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const requestRef = useRef(0);

    const validTenantId = Number.isInteger(tenantId) && tenantId > 0;
    const loadSummary = useCallback(async () => {
        if (!validTenantId) {
            setError("租户 ID 无效");
            setLoading(false);
            return;
        }
        const seq = ++requestRef.current;
        setLoading(true);
        setError("");
        try {
            const result = await apiClient.getManagedTenantSummary(tenantId);
            if (seq === requestRef.current) setSummary(result);
        } catch (loadError) {
            if (seq === requestRef.current) setError(loadError.message);
        } finally {
            if (seq === requestRef.current) setLoading(false);
        }
    }, [tenantId, validTenantId]);

    useEffect(() => {
        setSummary(null);
        setSelectedTab("overview");
        loadSummary();
        return () => { requestRef.current += 1; };
    }, [loadSummary]);

    const tenant = summary?.tenant;
    const tenantName = tenant?.name || `Tenant ${tenantId}`;
    const tabContent = useMemo(() => {
        if (!summary) return null;
        switch (selectedTab) {
            case "people":
                return <UserManagement tenantId={tenantId} tenantName={tenantName} />;
            case "customers":
                return (
                    <TenantCustomerManagement
                        tenantId={tenantId}
                        tenantName={tenantName}
                        accountPrefix={tenant?.accountPrefix || ""}
                    />
                );
            case "assets":
                return <ManagedAssetPanel tenantId={tenantId} tenantName={tenantName} onChanged={loadSummary} />;
            case "tasks":
                return <ManagedTaskPanel tenantId={tenantId} tenantName={tenantName} />;
            case "audit":
                return <ManagedAuditPanel tenantId={tenantId} />;
            default:
                return <WorkbenchOverview tenantId={tenantId} summary={summary} onRefresh={loadSummary} />;
        }
    }, [loadSummary, selectedTab, summary, tenant?.accountPrefix, tenantId, tenantName]);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div className={styles.title}>
                    <FC.Title2>{tenant?.seq || "-"} · {tenantName}</FC.Title2>
                    <span className={styles.managedBanner}>当前正在代管：{tenantName}</span>
                    <FC.Caption1>状态：{tenant?.status || "-"}</FC.Caption1>
                </div>
                <FC.Button icon={<IC.ArrowLeftRegular />} onClick={() => navigate("/system/tenants")}>
                    返回租户列表
                </FC.Button>
            </div>
            {tenant?.status && tenant.status !== "active" && (
                <FC.MessageBar intent="warning">
                    <FC.MessageBarBody>
                        该租户已停用：新建和数据提交会被服务端拒绝；设备状态与租期仍需在设备管理中维护。
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}
            {error && <FC.MessageBar intent="error"><FC.MessageBarBody>{error}</FC.MessageBarBody></FC.MessageBar>}
            {loading && <FC.Spinner label="加载租户代管概览…" />}
            {summary && (
                <>
                    <FC.TabList
                        selectedValue={selectedTab}
                        onTabSelect={(_, data) => setSelectedTab(data.value)}
                    >
                        {tabItems.map(([value, label]) => <FC.Tab key={value} value={value}>{label}</FC.Tab>)}
                    </FC.TabList>
                    <div className={styles.content} key={`${tenantId}:${selectedTab}`}>
                        {tabContent}
                    </div>
                </>
            )}
        </div>
    );
};
